import type { LinksFunction, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import type { SitemapArgs } from "remix-sitemap";
import { serverOnly$ } from "vite-env-only/macros";
import type { CmsPageParagraph } from "~types/api/cms.types";

import { CmsBlock } from "~/components/CmsBlock";
import { ErrorComponent, NotFoundPage } from "~/components/error-boundary";
import { PageContainer } from "~/components/ui/layout/page-container";
import { Wrapper } from "~/components/ui/layout/wrapper";
import { PageHeading } from "~/components/ui/page-heading";
import { getCmsPageBySlug } from "~/models/cms.server";
import { getSitemapCmsPages } from "~/models/sitemap.server";
import styles from "~/styles/AboutPage.css?url";
import { NotFoundPageError } from "~/utils/errors";
import { handleError } from "~/utils/errors.server";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = serverOnly$(async ({ request }: SitemapArgs) => {
  const pages = await getSitemapCmsPages({ request });
  if (!pages) {
    return [];
  }
  return pages.map((page) => ({
    loc: `/a-propos/${page.slug}`,
    exclude: !page || page.status !== 1,
  }));
});

export const meta: MetaFunction<typeof loader> = (metaArgs) => {
  const data = metaArgs.data;
  if (!data?.cmsData) {
    return [];
  }
  return getSeoMeta(metaArgs, {
    title: data.cmsData.title || data.cmsData.slug || "",
    description: data.cmsData.content || data.cmsData.title || data.cmsData.slug || "",
  });
};

export const links: LinksFunction = () => {
  return [{ rel: "stylesheet", href: styles }];
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  try {
    const pageSlug = params.pageSlug || "";
    const cmsData = await getCmsPageBySlug({ pageSlug, request });

    if (!cmsData) {
      throw new NotFoundPageError("Cette page n'existe pas ou n'est pas disponible");
    }

    if (cmsData.status !== 1) {
      throw new NotFoundPageError("Cette page n'existe pas ou n'est pas disponible");
    }

    return json({ cmsData, errors: null });
  } catch (error) {
    return handleError(error, null);
  }
};

const AboutPage = () => {
  const { cmsData } = useLoaderData<typeof loader>();

  const cmsParagraphs = cmsData?.cmsPageParagraphs;
  const breadcrumbData = [{ name: cmsData?.title || "", slug: "" }];
  return (
    <PageContainer>
      <PageHeading breadcrumbData={breadcrumbData} headingText={cmsData?.title || ""} />
      <Wrapper>
        {cmsData?.content ? <p className="AboutPage-description">{cmsData.content}</p> : null}
        {cmsParagraphs?.map((paragraph: CmsPageParagraph) => {
          return <CmsBlock cmsParagraph={paragraph} key={paragraph.content.toString()} />;
        })}
      </Wrapper>
    </PageContainer>
  );
};

export default AboutPage;

export const ErrorBoundary = () => {
  return (
    <ErrorComponent
      statusHandlers={{
        404: ({ error }) => <NotFoundPage>{error.data}</NotFoundPage>,
      }}
    />
  );
};
